import { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import AppLayout from '@amzn/meridian/app-layout';
import Masthead, { MastheadMenuButton, MastheadTitle } from '@amzn/meridian/masthead';
import Sheet from '@amzn/meridian/sheet';
import SideMenu, { SideMenuLink, SideMenuSpacer, SideMenuTitle } from '@amzn/meridian/side-menu';
import Responsive from '@amzn/meridian/responsive';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import chatIconTokens from '@amzn/meridian-tokens/base/icon/chat';
import desktopIconTokens from '@amzn/meridian-tokens/base/icon/desktop';
import gridIconTokens from '@amzn/meridian-tokens/base/icon/grid';
import plusCircleIconTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import searchIconTokens from '@amzn/meridian-tokens/base/icon/search';
import { css, injectGlobal } from '@emotion/css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './providers/AuthContext';
import { useSettings } from './providers/SettingsProvider';
import AgentChatSidebar from './components/AgentChatSidebar';
import IconButton from './components/IconButton';
import SidebarBrand from './components/SidebarBrand';
import SidebarFooter from './components/SidebarFooter';
import meridianOrangePalette from './theme/meridian-orange';
import Loading from './components/Loading';
import OnboardServiceModal from './components/OnboardServiceModal';
import ServiceList from './pages/ServiceList';
import ServiceSearch from './pages/ServiceSearch';
import ServiceDetail from './pages/ServiceDetail';
import JobStatus from './pages/JobStatus';
import { sidebarBackgrounds } from './theme/meridian-theme-tokens';
import { fadeUpEntrance, reducedMotionQuery } from './theme/motion';

const SIDEBAR_STORAGE_KEY = 'iris-catalog-sidebar-open';

injectGlobal({
  '[data-sidebar-theme="light"] #catalog-navigation': {
    backgroundImage: sidebarBackgrounds.light,
  },
  '[data-sidebar-theme="dark"] #catalog-navigation': {
    backgroundImage: sidebarBackgrounds.dark,
  },
  // Meridian has no token for the selected link's text, so the selected item is targeted through its
  // stable aria-current attribute to tint the label and icon with the theme color.
  '#catalog-navigation [aria-current="page"]': {
    color: meridianOrangePalette.palette500,
    fontWeight: 600,
  },
  '[data-sidebar-theme="dark"] #catalog-navigation [aria-current="page"]': {
    color: meridianOrangePalette.palette200,
  },
});

const pageEntranceStyles = css({
  width: '100%',
  minWidth: 0,
  animation: fadeUpEntrance,
  [reducedMotionQuery]: { animation: 'none' },
});

interface RouterProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

function getInitialDesktopMenuOpen(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false';
}

function formatUserName(user: ReturnType<typeof useAuth>['user']): string {
  if (!user) return 'Amazon user';

  if (user.name?.trim()) return user.name.trim();
  const fullName = [user.given_name, user.family_name].filter(Boolean).join(' ');
  if (fullName) return fullName;
  if (user.user_id) return user.user_id;
  return 'Amazon user';
}

function getBadgePhotoUrl(alias: string): string {
  return `https://cdn.prod.badgephotos.side.amazon.dev/?uid=${encodeURIComponent(alias)}`;
}

function createOnboardingHref(pathname: string, search: string, hash: string, open: boolean): string {
  const searchParams = new URLSearchParams(search);

  if (open) {
    searchParams.set('onboard', 'true');
  } else {
    searchParams.delete('onboard');
  }

  const nextSearch = searchParams.toString();
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}${hash}`;
}

function AppContent({ isDarkMode, onToggleTheme }: RouterProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(getInitialDesktopMenuOpen);
  const discoveryAgentEnabled = settings?.discoveryAgentEnabled === true;
  const isOnboardOpen = new URLSearchParams(location.search).get('onboard') === 'true';
  const isSearchRoute = !isOnboardOpen && (location.pathname === '/' || location.pathname === '/search');
  const isBrowseRoute =
    !isOnboardOpen && (location.pathname === '/services' || location.pathname.startsWith('/services/'));
  const openOnboardHref = createOnboardingHref(location.pathname, location.search, location.hash, true);
  const userName = formatUserName(user);
  const userAlias = user?.user_id;

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(desktopMenuOpen));
  }, [desktopMenuOpen]);

  const navigateTo = (href: string) => {
    setMobileMenuOpen(false);
    navigate(href);
  };

  const openOnboardService = () => {
    setMobileMenuOpen(false);
    navigate(openOnboardHref);
  };

  const openAssistant = () => {
    setMobileMenuOpen(false);
    setChatOpen(true);
  };

  const toggleAssistant = () => {
    setMobileMenuOpen(false);
    setChatOpen((currentChatOpen) => !currentChatOpen);
  };

  const closeOnboardService = () => {
    navigate(createOnboardingHref(location.pathname, location.search, location.hash, false), { replace: true });
  };

  return (
    <Responsive
      query="max-width"
      fallback="1024px"
      props={{
        isCompact: {
          default: false,
          '1100px': true,
        },
      }}
    >
      {({ isCompact }: { isCompact: boolean }) => (
        <AppLayout
          headerComponent={isCompact ? Masthead : undefined}
          sidebarComponent={SideMenu}
          sheetComponent={Sheet}
          backgroundColor="subdued"
          spacingInset={isCompact ? '300' : '450'}
          alwaysShowScrollbar
          skipNavigationMessage="Skip to catalog content"
        >
          {discoveryAgentEnabled && (
            <Sheet
              side="end"
              type={isCompact ? 'overlay' : 'push'}
              open={chatOpen}
              onClose={() => setChatOpen(false)}
              alwaysRenderChildren
              spacingInset="none"
            >
              <AgentChatSidebar onClose={() => setChatOpen(false)} />
            </Sheet>
          )}
          {isCompact && (
            <Masthead size="medium" backgroundColor="principal">
              <MastheadMenuButton
                open
                onClick={() => setMobileMenuOpen(true)}
                aria-controls="catalog-navigation"
                aria-expanded={mobileMenuOpen}
              >
                Open navigation
              </MastheadMenuButton>
              <MastheadTitle href="/search" onClick={navigateTo}>
                <Text type="h200">MCP Spec Catalog</Text>
              </MastheadTitle>
              {discoveryAgentEnabled && (
                <Tooltip position="bottom" title="Open Discovery Agent">
                  <IconButton iconTokens={chatIconTokens} label="Open Discovery Agent" onClick={openAssistant} />
                </Tooltip>
              )}
            </Masthead>
          )}
          <SideMenu
            id="catalog-navigation"
            data-sidebar-theme={isDarkMode ? 'dark' : 'light'}
            width={isCompact ? 280 : 240}
            navigationLabel="Catalog navigation"
            type={isCompact ? 'overlay' : 'skinny'}
            open={isCompact ? mobileMenuOpen : desktopMenuOpen}
            openLabel="Open navigation"
            closeLabel="Close navigation"
            onOpen={isCompact ? undefined : () => setDesktopMenuOpen(true)}
            onClose={isCompact ? () => setMobileMenuOpen(false) : () => setDesktopMenuOpen(false)}
          >
            <SideMenuTitle href="/search" onClick={navigateTo}>
              <SidebarBrand />
            </SideMenuTitle>
            <SideMenuLink href="/search" onClick={navigateTo} icon={searchIconTokens} selected={isSearchRoute}>
              Search
            </SideMenuLink>
            <SideMenuLink href="/services" onClick={navigateTo} icon={gridIconTokens} selected={isBrowseRoute}>
              Browse services
            </SideMenuLink>
            <SideMenuLink
              href={openOnboardHref}
              onClick={navigateTo}
              icon={plusCircleIconTokens}
              selected={isOnboardOpen}
            >
              Onboard service
            </SideMenuLink>
            {discoveryAgentEnabled && (
              <SideMenuLink onClick={toggleAssistant} icon={chatIconTokens} selected={chatOpen}>
                Discovery Agent
              </SideMenuLink>
            )}
            <SideMenuLink
              href="https://admin.iris.aft.amazon.dev/"
              target="_blank"
              rel="noopener noreferrer"
              icon={desktopIconTokens}
            >
              IRIS AI Studio
            </SideMenuLink>
            <SideMenuSpacer />
            <SidebarFooter
              isDarkMode={isDarkMode}
              onToggleTheme={onToggleTheme}
              userName={userName}
              userAlias={userAlias}
              profilePhotoUrl={userAlias ? getBadgePhotoUrl(userAlias) : undefined}
            />
          </SideMenu>
          <OnboardServiceModal open={isOnboardOpen} onClose={closeOnboardService} />
          <div key={location.pathname} className={pageEntranceStyles}>
            <Routes>
              <Route path="/" element={<ServiceSearch />} />
              <Route path="/search" element={<ServiceSearch />} />
              <Route path="/services" element={<ServiceList onOpenOnboard={openOnboardService} />} />
              <Route path="/services/:serviceName" element={<ServiceDetail />} />
              <Route path="/jobs/:serviceName" element={<JobStatus />} />
            </Routes>
          </div>
        </AppLayout>
      )}
    </Responsive>
  );
}

function Router(props: RouterProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Loading message="Redirecting to login..." />;
  }

  return <AppContent {...props} />;
}

export default Router;
